/**
 * The entry for the front page at `gibs.finance`: the "Open app" button
 * and the signed release links.
 *
 * WHAT THIS PAGE IS. A tiny static list of verified links to the interface,
 * in the shape of `scan.pulsechain.com`: a logo, a one-line decentralization
 * note, and a list of links. The interface itself lives only on IPFS — this
 * page never bundles it, never embeds it in an iframe, and loads nothing
 * from a third-party origin. The only network request this page ever makes
 * is to `ipfs.gibs.finance` for the signed release list itself (see
 * `config.ts`). It probes the reader's own computer (`127.0.0.1:7369`) for
 * a local copy of the interface only when the reader asks, or when a local
 * copy answered on an earlier visit (see `localCopyMemory.ts`).
 *
 * WHY VERIFICATION HAPPENS HERE AND NOT JUST ON THE SERVER. The Worker at
 * `ipfs.gibs.finance` already verifies the same manifest before it serves
 * `version.json` — but this page reads that file directly by `fetch()`, not
 * through the Worker's release selection, so it has no
 * reason to trust the bytes it receives without checking them again itself.
 * A reader who distrusts this page's own hosting can check the same
 * signature by hand — see the "Verify it yourself" section this page links.
 */
import { MANIFEST_URL, PUBLIC_KEYS_BY_KEY_ID, SIGNATURE_URL } from './config'
import { redirectLegacyHashRoute } from './legacyHashRedirect'
import { probeLocalCopy } from './localCopy'
import { showVerificationOutcome } from './page'
import { renderPage } from './render'
import { initializeThemeControl } from './theme'
import { verifyRelease } from './verify'
import { mountTopBar } from './topBar'
import './design-tokens.css'
import './theme.css'

const startPage = (): void => {
  const root = document.getElementById('app')
  if (root === null) {
    throw new Error('the landing page is missing its root element')
  }

  mountTopBar(document.getElementById('top-bar'))
  initializeThemeControl(document)

  renderPage(root, { kind: 'loading' })

  void verifyRelease(MANIFEST_URL, SIGNATURE_URL, PUBLIC_KEYS_BY_KEY_ID).then((outcome) => {
    showVerificationOutcome(root, outcome, { probeLocalCopy: () => probeLocalCopy() })
  })
}

// An old deep link (`gibs.finance/#/bridge/...`) goes straight to the app.
if (!redirectLegacyHashRoute(window.location)) {
  startPage()
}
